import { Config } from '../../video-data/config';
import type { Asset } from '../asset/BaseAsset';
import type { TimeAwareMediaAsset } from '../asset/TimeAwareMediaAsset';
import type { Slide } from '../slide/Slide';
import type { CircleMask } from '../mask/CircleMask';
import type { PolygonMask } from '../mask/PolygonMask';
import type { StencilMask } from '../mask/StencilMask';

export type MorphableProperty =
    | 'position.x'
    | 'position.y'
    | 'scale.x'
    | 'scale.y'
    | 'alpha'
    | 'mask.transition'
    | 'scaleX'
    | 'scaleY'
    | 'xTranslation'
    | 'yTranslation';

type MorphSourceState = {
    asset: Asset;
    x: number;
    y: number;
    scaleX: number;
    scaleY: number;
    alpha: number;
};

export class MorphManager {
    #slide: Slide;

    static #mediaTimeOffsets: Map<string, number> = new Map();
    static #allTargetAssets: Map<string, Asset> = new Map();
    static #morphPairs: Map<string, string> = new Map();
    static #reverseMorphPairs: Map<string, string> = new Map();
    static #isUpdating = false;

    static getMediaTimeOffset(assetId: string): number | null {
        return MorphManager.#mediaTimeOffsets.get(assetId) ?? null;
    }

    static isMorphTarget(assetId: string): boolean {
        return MorphManager.#allTargetAssets.has(assetId);
    }

    #sourceStates: Map<string, MorphSourceState> = new Map();
    #targetAssets: Set<Asset> = new Set();

    constructor(slide: Slide) {
        this.#slide = slide;
    }

    setupMorphAnimations() {
        if (MorphManager.#isUpdating) {
            return;
        }

        const nextScene = this.#slide.nextScene;
        if (!nextScene) {
            return;
        }

        MorphManager.#isUpdating = true;
        try {
            const sourceAssets = this.#slide
                .getAssets({ deep: true })
                .sort((a, b) => a.name.localeCompare(b.name));
            const targetAssets = nextScene
                .getAssets({ deep: true })
                .sort((a, b) => a.name.localeCompare(b.name));

            sourceAssets.forEach((sourceAsset) => {
                const matchingAsset = targetAssets.find(
                    (targetAsset) =>
                        !this.#targetAssets.has(targetAsset) &&
                        sourceAsset.isMorphMatch(targetAsset),
                );
                if (!matchingAsset) {
                    return;
                }

                this.#setupMorphForAssetPair(sourceAsset, matchingAsset);
            });

            for (const { asset } of this.#sourceStates.values()) {
                if (!asset.hasMorphAnimation) {
                    continue;
                }
                MorphManager.#forceBuildAndDraw(asset);
            }
        } finally {
            MorphManager.#isUpdating = false;
        }
    }

    clearMorphAnimations() {
        if (MorphManager.#isUpdating) {
            return;
        }

        MorphManager.#isUpdating = true;
        try {
            for (const state of this.#sourceStates.values()) {
                const { asset, x, y, scaleX, scaleY, alpha } = state;

                if (!asset || asset.destroyed) {
                    continue;
                }

                asset.clearMorphAnimations();

                asset.transformation.position.x = x;
                asset.transformation.position.y = y;
                asset.scale.x = scaleX;
                asset.scale.y = scaleY;
                asset.alpha = alpha;

                if (asset.mask && Config.isStencilMask(asset.mask)) {
                    (asset.mask as CircleMask | PolygonMask)._clearMorphState();
                }

                MorphManager.#forceBuildAndDraw(asset);
            }
            for (const sourceId of this.#sourceStates.keys()) {
                const targetId = MorphManager.#morphPairs.get(sourceId);
                if (targetId) {
                    MorphManager.#reverseMorphPairs.delete(targetId);
                }
                MorphManager.#morphPairs.delete(sourceId);
            }
            this.#sourceStates.clear();

            for (const targetAsset of this.#targetAssets) {
                MorphManager.#mediaTimeOffsets.delete(targetAsset.id);
                MorphManager.#allTargetAssets.delete(targetAsset.id);
                if (!targetAsset.destroyed) {
                    MorphManager.#forceBuildAndDraw(targetAsset);
                }
            }
            this.#targetAssets.clear();
        } finally {
            MorphManager.#isUpdating = false;
        }
    }

    #setupMorphForAssetPair(sourceAsset: Asset, targetAsset: Asset) {
        const { duration: morphDuration, interpolation } = sourceAsset._morphParameters;

        if (this.#slide.duration < morphDuration) {
            return;
        }

        if (!this.#sourceStates.has(sourceAsset.id)) {
            this.#sourceStates.set(sourceAsset.id, {
                asset: sourceAsset,
                x: sourceAsset.transformation.position.x,
                y: sourceAsset.transformation.position.y,
                scaleX: sourceAsset.scale.x,
                scaleY: sourceAsset.scale.y,
                alpha: sourceAsset.alpha,
            });
        }

        this.#targetAssets.add(targetAsset);
        MorphManager.#allTargetAssets.set(targetAsset.id, targetAsset);
        MorphManager.#morphPairs.set(sourceAsset.id, targetAsset.id);
        MorphManager.#reverseMorphPairs.set(targetAsset.id, sourceAsset.id);

        if (Config.isMediaAsset(targetAsset) || Config.isIconAsset(targetAsset)) {
            MorphManager.#mediaTimeOffsets.set(targetAsset.id, this.#slide.duration);
        }

        MorphManager.#forceBuildAndDraw(sourceAsset);
        MorphManager.#forceBuildAndDraw(targetAsset);

        const animationConfigs = (() => {
            const assetConfigs = MorphManager.#buildAssetAnimationConfigs(sourceAsset, targetAsset);

            const hasMaskMorph = MorphManager.#setupMaskMorphState(sourceAsset, targetAsset);
            if (!hasMaskMorph) {
                return assetConfigs;
            }

            const maskConfigs = MorphManager.#buildMaskAnimationConfigs(sourceAsset, targetAsset);
            return { ...assetConfigs, ...maskConfigs };
        })();

        const excludedProperties = sourceAsset.morphExcludedProperties;
        Object.entries(animationConfigs).forEach(([morphableProperty, config]) => {
            if (excludedProperties.includes(morphableProperty as MorphableProperty)) {
                return;
            }

            const sourceAnim = sourceAsset.addMorphAnimation();
            sourceAnim.property = config.property;
            sourceAnim.startValue = config.startValue;
            sourceAnim.endValue = config.endValue;
            sourceAnim.duration = morphDuration;
            sourceAnim.interpolation = interpolation;
            sourceAnim.shouldOffsetPropertyValue = config.shouldOffsetPropertyValue;
        });
    }

    static #buildAssetAnimationConfigs(sourceAsset: Asset, targetAsset: Asset) {
        const configs: Partial<
            Record<
                MorphableProperty,
                {
                    property: string;
                    shouldOffsetPropertyValue: boolean;
                    startValue: number;
                    endValue: number;
                }
            >
        > = {};

        if (sourceAsset.alpha !== targetAsset.alpha) {
            configs['alpha'] = {
                property: 'alpha',
                shouldOffsetPropertyValue: false,
                startValue: sourceAsset.alpha,
                endValue: targetAsset.alpha,
            };
        }

        // Asset with Mask uses other properties for scale and position morph
        if (sourceAsset.mask) {
            return configs;
        }

        const sourcePos = sourceAsset.transformation.position;
        const targetPos = targetAsset.transformation.position;

        const deltaX = targetPos.x - sourcePos.x;
        if (deltaX !== 0) {
            configs['position.x'] = {
                property: 'transformation.position.x',
                shouldOffsetPropertyValue: true,
                startValue: 0,
                endValue: deltaX,
            };
        }

        const deltaY = targetPos.y - sourcePos.y;
        if (deltaY !== 0) {
            configs['position.y'] = {
                property: 'transformation.position.y',
                shouldOffsetPropertyValue: true,
                startValue: 0,
                endValue: deltaY,
            };
        }

        const scaleRatioX = sourceAsset.width > 0 ? targetAsset.width / sourceAsset.width : 1;
        if (scaleRatioX !== 1) {
            configs['scale.x'] = {
                property: 'scale.x',
                shouldOffsetPropertyValue: false,
                startValue: 1,
                endValue: scaleRatioX,
            };
        }

        const scaleRatioY = sourceAsset.height > 0 ? targetAsset.height / sourceAsset.height : 1;
        if (scaleRatioY !== 1) {
            configs['scale.y'] = {
                property: 'scale.y',
                shouldOffsetPropertyValue: false,
                startValue: 1,
                endValue: scaleRatioY,
            };
        }

        return configs;
    }

    static #buildMaskAnimationConfigs(sourceAsset: Asset, targetAsset: Asset) {
        const configs: Partial<
            Record<
                MorphableProperty,
                {
                    property: string;
                    shouldOffsetPropertyValue: boolean;
                    startValue: number;
                    endValue: number;
                }
            >
        > = {};

        configs['mask.transition'] = {
            property: 'mask.transition',
            shouldOffsetPropertyValue: false,
            startValue: 0,
            endValue: 1,
        };

        const sourceMedia = sourceAsset as TimeAwareMediaAsset;
        const targetMedia = targetAsset as TimeAwareMediaAsset;

        if (sourceMedia.scaleX !== targetMedia.scaleX) {
            configs['scaleX'] = {
                property: 'scaleX',
                shouldOffsetPropertyValue: false,
                startValue: sourceMedia.scaleX,
                endValue: targetMedia.scaleX,
            };
        }

        if (sourceMedia.scaleY !== targetMedia.scaleY) {
            configs['scaleY'] = {
                property: 'scaleY',
                shouldOffsetPropertyValue: false,
                startValue: sourceMedia.scaleY,
                endValue: targetMedia.scaleY,
            };
        }

        if (sourceMedia.xTranslation !== targetMedia.xTranslation) {
            configs['xTranslation'] = {
                property: 'xTranslation',
                shouldOffsetPropertyValue: false,
                startValue: sourceMedia.xTranslation,
                endValue: targetMedia.xTranslation,
            };
        }

        if (sourceMedia.yTranslation !== targetMedia.yTranslation) {
            configs['yTranslation'] = {
                property: 'yTranslation',
                shouldOffsetPropertyValue: false,
                startValue: sourceMedia.yTranslation,
                endValue: targetMedia.yTranslation,
            };
        }

        return configs;
    }

    static #setupMaskMorphState(sourceAsset: Asset, targetAsset: Asset): boolean {
        const sourceMask = sourceAsset.mask as StencilMask;
        const targetMask = targetAsset.mask as StencilMask;

        if (!sourceMask || !targetMask) {
            return false;
        }

        if (Config.isCircleMask(sourceMask) && Config.isCircleMask(targetMask)) {
            const sourceCircle = sourceMask as CircleMask;
            const targetCircle = targetMask as CircleMask;

            sourceCircle._setMorphState({
                startRadius: sourceCircle.endRadius,
                endRadius: targetCircle.endRadius,
                startCenter: { x: sourceCircle.centerX, y: sourceCircle.centerY },
                endCenter: { x: targetCircle.centerX, y: targetCircle.centerY },
            });
            sourceCircle.transition = 0;
            return true;
        }

        if (Config.isPolygonMask(sourceMask) && Config.isPolygonMask(targetMask)) {
            const sourcePolygon = sourceMask as PolygonMask;
            const targetPolygon = targetMask as PolygonMask;

            if (sourcePolygon.endPoints.length !== targetPolygon.endPoints.length) {
                return false;
            }

            const orderedSourceEndPoints = MorphManager.#orderPolygonPointsByCentroid(
                sourcePolygon.endPoints,
            );
            const orderedTargetEndPoints = MorphManager.#orderPolygonPointsByCentroid(
                targetPolygon.endPoints,
            );

            if (orderedSourceEndPoints.some(isNaN) || orderedTargetEndPoints.some(isNaN)) {
                return false;
            }

            sourcePolygon._setMorphState({
                startPoints: orderedSourceEndPoints,
                endPoints: orderedTargetEndPoints,
            });
            sourcePolygon.transition = 0;
            return true;
        }

        return false;
    }

    static #orderPolygonPointsByCentroid(points: number[]): number[] {
        const numPoints = points.length / 2;

        let centroidX = 0;
        let centroidY = 0;
        for (let i = 0; i < numPoints; i++) {
            centroidX += points[i * 2];
            centroidY += points[i * 2 + 1];
        }
        centroidX /= numPoints;
        centroidY /= numPoints;

        const pointsArray = Array.from({ length: numPoints }, (_, i) => {
            const x = points[i * 2];
            const y = points[i * 2 + 1];
            const angle = Math.atan2(y - centroidY, x - centroidX);
            return { x, y, angle };
        });

        pointsArray.sort((a, b) => a.angle - b.angle);

        return pointsArray.flatMap((point) => [point.x, point.y]);
    }

    static updateMorphAnimations(scenes: Slide[]) {
        const hasActiveTextEditing = scenes.some((scene) =>
            scene.getAssetsByType(Config.TEXT_ASSET_TYPE).some((a) => a.hasTextEditingEnabled),
        );
        if (hasActiveTextEditing) return;

        scenes.forEach((scene) => {
            scene.clearMorphAnimations();
        });

        scenes.forEach((scene) => {
            if (scene.morphEnabled) {
                scene._setupMorphAnimations();
            }
        });
    }

    static propagateLinkedProperties(modifiedScene: Slide) {
        if (MorphManager.#isUpdating) {
            return;
        }
        MorphManager.#isUpdating = true;
        try {
            let current = modifiedScene;
            while (current.morphEnabled && current.nextScene) {
                MorphManager.#propagateBetweenScenes(current, current.nextScene);
                current = current.nextScene;
            }

            current = modifiedScene;
            while (current.prevScene?.morphEnabled) {
                MorphManager.#propagateBetweenScenes(current, current.prevScene);
                current = current.prevScene;
            }
        } finally {
            MorphManager.#isUpdating = false;
        }
    }

    static #propagateBetweenScenes(fromScene: Slide, toScene: Slide) {
        const fromAssets = fromScene.getAssets({ deep: true });
        const toAssetMap = new Map(toScene.getAssets({ deep: true }).map((a) => [a.id, a]));

        for (const fromAsset of fromAssets) {
            const targetId = MorphManager.#morphPairs.get(fromAsset.id);
            if (targetId) {
                const target = toAssetMap.get(targetId);
                if (target?.syncMorphLinkedProperties(fromAsset)) {
                    target.requiresRedraw = true;
                }
            }

            const sourceId = MorphManager.#reverseMorphPairs.get(fromAsset.id);
            if (sourceId) {
                const source = toAssetMap.get(sourceId);
                if (source?.syncMorphLinkedProperties(fromAsset)) {
                    source.requiresRedraw = true;
                }
            }
        }
    }

    static #forceBuildAndDraw(asset: Asset) {
        const prevAutoDraw = asset.autoDraw;
        asset.autoDraw = true;
        asset.requiresRedraw = true;
        asset._buildAndDrawRecur();
        asset.autoDraw = prevAutoDraw;
    }
}
